/**
 * WorkdayShell.js
 *
 * Presentation shells shared by the Munkanap surfaces. Both follow the premium modal blueprint
 * (docs/PREMIUM_MODAL_BLUEPRINT.md): SOLID surface (no glass), soft dim backdrop, navy gradient header accent, orange
 * brand, pill radii and card radius 28.
 * - {@link WorkdaySheetShell}  bottom sheet (status sheet, start sheet),
 * - {@link WorkdayCardShell}   centred card, maxWidth ~420 (end-of-day reminder).
 *
 * @module components/workday/WorkdayShell
 */

import React from 'react';
import {Modal, Platform, Pressable, StyleSheet, View} from 'react-native';
import {useManagementTheme} from '../../helpers/managementTheme';
import {useBottomInset, useTopInset} from '../../helpers/safeArea';

/** Solid dim backdrop colour. */
export const BACKDROP = 'rgba(8,12,24,0.58)';

/**
 * Bottom sheet (not full screen).
 * @param {object} props props.
 * @param {boolean} props.visible shown.
 * @param {() => void} props.onClose backdrop tap / Android back.
 * @param {React.ReactNode} props.children content.
 * @returns {JSX.Element} modal.
 */
export function WorkdaySheetShell({visible, onClose, children}) {
    const m = useManagementTheme();
    const bottom = useBottomInset();
    const top = useTopInset();

    return (
        <Modal visible={visible} transparent animationType="slide" statusBarTranslucent={Platform.OS === 'android'} onRequestClose={onClose}>
            <View style={styles.sheetRoot}>
                <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityRole="button" />
                <View style={[styles.sheet, {backgroundColor: m.colors.surface, paddingBottom: Math.max(bottom, 14) + 6, maxHeight: '92%', marginTop: top}]}>
                    <View style={[styles.handle, {backgroundColor: m.colors.border}]} />
                    {children}
                </View>
            </View>
        </Modal>
    );
}

/**
 * Centred card modal (not full screen).
 * @param {object} props props.
 * @param {boolean} props.visible shown.
 * @param {() => void} props.onClose backdrop tap / Android back.
 * @param {React.ReactNode} props.children content.
 * @returns {JSX.Element} modal.
 */
export function WorkdayCardShell({visible, onClose, children}) {
    const m = useManagementTheme();

    return (
        <Modal visible={visible} transparent animationType="fade" statusBarTranslucent={Platform.OS === 'android'} onRequestClose={onClose}>
            <View style={styles.cardRoot}>
                <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityRole="button" />
                <View style={[styles.card, {backgroundColor: m.colors.surface, borderColor: m.colors.border}, m.shadow(3)]}>{children}</View>
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    sheetRoot: {flex: 1, justifyContent: 'flex-end', backgroundColor: BACKDROP},
    sheet: {width: '100%', maxWidth: 560, alignSelf: 'center', borderTopLeftRadius: 28, borderTopRightRadius: 28, paddingHorizontal: 20, paddingTop: 10},
    handle: {alignSelf: 'center', width: 44, height: 5, borderRadius: 3, marginBottom: 12},
    cardRoot: {flex: 1, alignItems: 'center', justifyContent: 'center', padding: 20, backgroundColor: BACKDROP},
    card: {width: '100%', maxWidth: 420, borderRadius: 28, borderWidth: 1, overflow: 'hidden'},
});
